import { RequirePermission } from "@components/common/require-permission";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { BaseMemberProps } from "@custom-types/base-types";
import { Step, StepContent, StepLabel, Stepper } from "@mui/material";
import { Box } from "@mui-wrapper";
import { useAppNavigation } from "@router/use-app-navigation";
import { MemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { SetStateAction, useCallback, useMemo, useState } from "react";
import { deleteMemberSteps } from "@components/delete-member/delete-member-utils";
import { Button, grey, red, Dialog, Text } from "@flat-ui";
import { ActionButtonSx } from "@styles/common-styles";
import { getMemberDisplayName } from "@utils/data-display";

/** Component to delete a member from the whole FARO ecosystem */
export function DeleteMember({ member }: BaseMemberProps): React.JSX.Element {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
  const [activeStepper, setActiveStepper] = useState<number>(0);
  const [shouldDisableContinueButton, setShouldDisableContinueButton] =
    useState<boolean>(false);
  const [isDeletingMember, setIsDeletingMember] = useState<boolean>(false);

  const { trackEvent } = useTrackEvent();
  const { handleErrorWithToast } = useErrorHandler();
  const { navigateToHomePage } = useAppNavigation();

  const steps = useMemo(
    () => deleteMemberSteps({ member, setShouldDisableContinueButton }),
    [member]
  );

  /** Flag whether the last step is active */
  const isLastStep = activeStepper === steps.length - 1;

  /** Sets a new step in the dialog */
  const setNewStep = useCallback(
    (newStep: SetStateAction<number>): void => {
      /** Calculate the new step number */
      const stepNumber =
        typeof newStep === "function" ? newStep(activeStepper) : newStep;

      setActiveStepper(stepNumber);

      // Track after the step has been set
      trackEvent({
        name: MemberEvents.deleteMemberStep,
        props: {
          // Since the step number is 0 indexed, we add 1 to it to make it more human readable in the analytics
          activeStep: stepNumber + 1,
        },
      });
    },
    [activeStepper, trackEvent, setActiveStepper]
  );

  /**
   * Determines the secondary action for the dialog.
   * If it's the first step or if the member is currently being deleted, there is no secondary action (returns undefined).
   * Otherwise, it returns a function that sets the previous step as the active step when called.
   */
  const secondaryAction = useMemo(() => {
    if (activeStepper === 0 || isDeletingMember) {
      return undefined;
    }
    return () => {
      setNewStep((prevActiveStep) => prevActiveStep - 1);
    };
  }, [activeStepper, isDeletingMember, setNewStep]);

  /** Closes the dialog and resets its state */
  function closeDialog(): void {
    setActiveStepper(0);
    setShouldDisableContinueButton(false);
    setIsDeletingMember(false);
    setIsDialogOpen(false);
  }

  /**
   * Handles the deletion of a member. It tracks the event, calls the API to delete the member,
   * Regardless of the result, the dialog is closed at the end. If the deletion is successful, a success dialog is opened.
   * If there's an error, it is handled and displayed as a toast notification.
   */
  async function onDeleteMember(): Promise<void> {
    trackEvent({ name: MemberEvents.deleteMember });

    try {
      setIsDeletingMember(true);
      await faroCoreApiClient.admin.deleteMember(member.identity);

      setIsSuccessDialogOpen(true);
    } catch (error) {
      handleErrorWithToast({
        id: `delete-member-${Date.now().toString()}`,
        title: "Could not delete member",
        error,
      });
    } finally {
      closeDialog();
    }
  }

  /**
   * Triggered when the user clicks on confirm. If it's the last step, it calls the function to delete the member.
   * Otherwise, it proceeds to the next step in the dialog.
   */
  function onConfirmClicked(): void {
    if (isLastStep) {
      void onDeleteMember();
      return;
    }
    setNewStep((prevActiveStep) => prevActiveStep + 1);
  }

  return (
    <Box>
      <RequirePermission permission="member.deleteMember">
        <Button
          variant="destructiveGhost"
          sx={{
            ...ActionButtonSx,
            color: red[500],
            border: "1px solid red",
            "&:hover": {
              backgroundColor: `${grey[500]}26`,
              border: "1px solid red",
            },
            "&:active": {
              backgroundColor: `${grey[500]}40`,
              border: `1px solid ${grey[500]}40`,
            },
          }}
          onClick={() => {
            trackEvent({ name: MemberEvents.openDeleteMemberDialog });
            setIsDialogOpen(true);
          }}
        >
          Delete Member
        </Button>
      </RequirePermission>

      {/* Dialog to show steps to delete a member and confirm the action */}
      <Dialog
        open={isDialogOpen}
        title={`Are you sure you want to delete ${getMemberDisplayName(
          member
        )}?`}
        onSecondary={secondaryAction}
        secondaryText={secondaryAction && "Back"}
        onCancel={isDeletingMember ? undefined : () => closeDialog()}
        onConfirm={onConfirmClicked}
        isConfirmDisabled={shouldDisableContinueButton}
        showSpinner={isDeletingMember}
        confirmText={isLastStep ? "Delete User" : "Continue"}
        size="l"
      >
        <Stepper activeStep={activeStepper} orientation="vertical">
          {steps.map((step, index) => (
            <Step key={`step${index}`}>
              <StepLabel>{step.label}</StepLabel>
              <StepContent>{step.content}</StepContent>
            </Step>
          ))}
        </Stepper>
      </Dialog>

      {/* Success dialog after member deletion, with a button to navigate back to the home page */}
      <Dialog
        title="Member Deleted"
        open={isSuccessDialogOpen}
        onConfirm={() => navigateToHomePage()}
        confirmText="Close"
      >
        <Text variant="bodyL">
          The member has been successfully deleted from the FARO ecosystem.
          Please click on close to navigate to home page.
        </Text>
      </Dialog>
    </Box>
  );
}
